import {StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import IconButton from "@khanacademy/wonder-blocks-icon-button";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";
import Tooltip from "@khanacademy/wonder-blocks-tooltip";
import {doNotTranslateYet} from "@khan/i18n";
import {mediaQueries} from "@khan/shared-styles";

import type {ComputingCodeMultiFile} from "#content-library/types/types.ts";

import CodeMirrorEditor from "./code-mirror-editor.tsx";
import FileTab from "./file-tab.tsx";
import FilenameEntryForm from "./filename-entry-form.tsx";
import {getNextFilename, getPrevFilename} from "./files.ts";
import {mapErrorCodeToSeverity} from "./map-error-code-to-severity.ts";
import MobileFileSelect from "./mobile-file-select.tsx";
import ScreenReaderToast, {
    type ScreenReaderToastRefHandle,
} from "./screen-reader-toast.tsx";
import type {SelectMenuState} from "./select-menu.tsx";
import * as strings from "./strings.tsx";
import {useRuffLinter} from "./use-ruff-linter.ts";
import * as utils from "./utils.ts";

import plusCircleIcon from "@phosphor-icons/core/regular/plus-circle.svg";

export const fileCountLimit = 10;

type Props = {
    // The code for all the files to be edited. This is only used
    // when the component is first mounted, so passing in a new value will have
    // no effect. To render a new set of code, render a new instance of the
    // TabbedCodeEditor component, perhaps by using the key prop.
    initialCode: ComputingCodeMultiFile;

    // A callback that will be called on each change to any of
    // the files in the code.
    onCodeChange: (code: ComputingCodeMultiFile) => void;

    // The name of the currently-selected file. If the filename is not found
    // in any of the files given in the initialCode prop, no editor will be
    // rendered.
    selectedFilename: string;

    // This function will be called with the name of the newly-selected file
    // whenever the user changes the selected file with the file tabs.
    onSelectedFilenameChange: (filename: string) => void;

    // True if the user is allowed to create, delete, and rename files.
    // Defaults to false.
    canManageFiles?: boolean;

    // Props related to the mobile file select menu. The parent controls whether
    // the menu is displayed (likely via a button with role="listbox"), but this
    // component actually renders it. This should be generated by
    // useSelectMenuState, which is in select-menu.tsx.
    mobileFileSelect?: SelectMenuState;

    // True if the program is being displayed as an embedded program.
    useEmbeddedStyles?: boolean;
};

/**
 * A tabbed code editor that allows the user to edit multiple files at once.
 */
export default function TabbedCodeEditor({
    initialCode,
    onCodeChange,
    selectedFilename,
    onSelectedFilenameChange,
    canManageFiles,
    mobileFileSelect,
    useEmbeddedStyles,
}: Props): React.ReactElement {
    const cachedCode = React.useRef<ComputingCodeMultiFile>(initialCode);
    const handleCodeChange = React.useCallback(
        (filename: string, updatedCode: string) => {
            // We create a copy of the cached code so that we don't mutate the
            // original initialCode object, which can have weird side effects!
            // See this PR for more details: https://github.com/Khan/webapp/pull/17377
            cachedCode.current = {
                files: cachedCode.current.files.map((file) => {
                    if (file.filename === filename) {
                        return {
                            ...file,
                            code: updatedCode,
                        };
                    }
                    return file;
                }),
            };
            onCodeChange(cachedCode.current);
        },
        [onCodeChange],
    );
    const createFile = (filename: string) => {
        cachedCode.current = utils.createFile(cachedCode.current, filename);
        onCodeChange(cachedCode.current);
    };

    // maps a filename to a stable key for the file, which does not change if
    // the file is renamed.
    const fileKeysMapRef = React.useRef(new Map<string, number>());

    const deleteFile = (filename: string) => {
        const filenameIndex = cachedCode.current.files.findIndex(
            (file) => file.filename === filename,
        );
        cachedCode.current = utils.deleteFile(cachedCode.current, filename);
        onCodeChange(cachedCode.current);
        fileKeysMapRef.current.delete(filename);
        if (filename === selectedFilename) {
            onSelectedFilenameChange(
                cachedCode.current.files[filenameIndex - 1].filename,
            );
        }
    };

    const renameFile = (oldFilename: string, newFilename: string) => {
        if (oldFilename === newFilename) {
            return;
        }

        cachedCode.current = utils.renameFile(
            cachedCode.current,
            oldFilename,
            newFilename,
        );
        onCodeChange(cachedCode.current);
        const fileKey = fileKeysMapRef.current.get(oldFilename);
        if (fileKey != null) {
            fileKeysMapRef.current.set(newFilename, fileKey);
            fileKeysMapRef.current.delete(oldFilename);
        }
        if (oldFilename === selectedFilename) {
            onSelectedFilenameChange(newFilename);
        }
    };

    const lastFileKeyRef = React.useRef(0);
    let fileKey = fileKeysMapRef.current.get(selectedFilename);
    if (fileKey == null) {
        fileKey = lastFileKeyRef.current + 1;
        lastFileKeyRef.current = fileKey;
        fileKeysMapRef.current.set(selectedFilename, fileKey);
    }

    const [isCreatingFile, setIsCreatingFile] = React.useState(false);

    const {check} = useRuffLinter();
    const handleLintCode = React.useCallback(
        async (code: string) => {
            const diagnostics = await check(code);
            return diagnostics.map(
                ({
                    code: errorCode,
                    message,
                    location: start,
                    end_location: end,
                }) => ({
                    message,
                    severity: mapErrorCodeToSeverity(errorCode),
                    start,
                    end,
                }),
            );
        },
        [check],
    );

    const toastRef = React.useRef<ScreenReaderToastRefHandle | null>(null);
    const tabHeaderRef = React.useRef<HTMLElement | null>(null);

    // If a tab is selected, the Tab key moves into the code editor and the left
    // and right arrow keys switch tabs.
    const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
        let handled = true;
        switch (event.key) {
            case "ArrowLeft":
                onSelectedFilenameChange(
                    getPrevFilename(cachedCode.current, selectedFilename),
                );
                break;
            case "ArrowRight":
                onSelectedFilenameChange(
                    getNextFilename(cachedCode.current, selectedFilename),
                );
                break;
            case "Home":
                onSelectedFilenameChange(cachedCode.current.files[0].filename);
                break;
            case "End":
                onSelectedFilenameChange(
                    cachedCode.current.files[
                        cachedCode.current.files.length - 1
                    ].filename,
                );
                break;
            default:
                handled = false;
                break;
        }

        if (handled) {
            event.preventDefault();
            event.stopPropagation();
        }
    };

    const previousFilenameRef = React.useRef<string>(selectedFilename);
    const shouldSkipFocusRef = React.useRef(false);

    const shouldFocusSelectedTabRef = React.useRef(false);
    shouldFocusSelectedTabRef.current =
        previousFilenameRef.current !== selectedFilename &&
        !shouldSkipFocusRef.current;

    previousFilenameRef.current = selectedFilename;
    shouldSkipFocusRef.current = false;

    const initialCodeForCurrentFile = React.useMemo(() => {
        return cachedCode.current.files.find(
            (file) => file.filename === selectedFilename,
        )?.code;
    }, [selectedFilename]);

    const filenames = new Set(cachedCode.current.files.map((f) => f.filename));

    const styles = useEmbeddedStyles ? embeddedStyles : standaloneStyles;

    return (
        <View style={styles.tabsContainer}>
            <h3 className="sr-only" id="code-tabs-title">
                {doNotTranslateYet`Code files`}
            </h3>
            <View style={styles.files}>
                <View
                    style={styles.tabHeader}
                    role="tablist"
                    aria-labelledby="code-tabs-title"
                    ref={tabHeaderRef}
                >
                    {cachedCode.current.files.map((file, i) => {
                        const {filename} = file;
                        const isSelected = filename === selectedFilename;
                        const allowRenameAndDelete = canManageFiles && i !== 0;

                        return (
                            <FileTab
                                key={filename}
                                filename={filename}
                                isSelected={isSelected}
                                onSelect={() =>
                                    onSelectedFilenameChange(filename)
                                }
                                onKeyDown={onKeyDown}
                                filenames={filenames}
                                onRenameAborted={() =>
                                    toastRef.current?.toast(
                                        strings.renameAborted(filename),
                                    )
                                }
                                onRename={
                                    allowRenameAndDelete
                                        ? (newName, blurEvent) => {
                                              renameFile(filename, newName);
                                              toastRef.current?.toast(
                                                  strings.fileRenamed(
                                                      filename,
                                                      newName,
                                                  ),
                                              );
                                              if (blurEvent != null) {
                                                  // If the filename was submitted by the user unfocusing the form,
                                                  // we want to avoid focusing the newly renamed tab.
                                                  shouldSkipFocusRef.current =
                                                      true;
                                              }
                                          }
                                        : undefined
                                }
                                onDelete={
                                    allowRenameAndDelete
                                        ? () => {
                                              deleteFile(filename);
                                              toastRef.current?.toast(
                                                  strings.fileDeleted(filename),
                                              );
                                          }
                                        : undefined
                                }
                                ref={
                                    isSelected
                                        ? (tabButton) => {
                                              if (
                                                  shouldFocusSelectedTabRef.current &&
                                                  tabButton != null
                                              ) {
                                                  // when the selected tab changes, we focus the button for
                                                  // the newly selected tab. we do that in the ref callback so
                                                  // that it happens after render and before paint, but
                                                  // without calling useLayoutEffect since that triggers a
                                                  // false positive error log during SSR which makes the
                                                  // snapshot tests fail.
                                                  tabButton.focus();
                                                  shouldFocusSelectedTabRef.current =
                                                      false;
                                              }
                                          }
                                        : undefined
                                }
                            />
                        );
                    })}
                    {isCreatingFile && (
                        <FilenameEntryForm
                            isSelected={false}
                            filenames={filenames}
                            onSubmit={(filename, blurEvent) => {
                                createFile(filename);
                                toastRef.current?.toast(
                                    strings.fileCreated(filename),
                                );
                                setIsCreatingFile(false);
                                if (
                                    blurEvent != null &&
                                    tabHeaderRef.current?.contains(
                                        blurEvent.relatedTarget,
                                    )
                                ) {
                                    // If the user just focused a different tab button, we don't want
                                    // to automatically select the newly created tab.
                                    return;
                                }
                                onSelectedFilenameChange(filename);
                                if (blurEvent != null) {
                                    // If the filename was submitted by the user unfocusing the form,
                                    // we want to avoid focusing the newly created tab.
                                    shouldSkipFocusRef.current = true;
                                }
                            }}
                            onCancel={() => {
                                toastRef.current?.toast(
                                    doNotTranslateYet`Creating file aborted`,
                                );
                                setIsCreatingFile(false);
                            }}
                        />
                    )}
                </View>
                {canManageFiles && (
                    <CreateFileButton
                        onClick={() => setIsCreatingFile(true)}
                        isCreatingFile={isCreatingFile}
                        hasReachedFileLimit={
                            cachedCode.current.files.length >= fileCountLimit
                        }
                    />
                )}
            </View>
            <CodeMirrorEditor
                initialCode={initialCodeForCurrentFile ?? ""}
                filename={selectedFilename}
                fileKey={fileKey}
                onLintCode={handleLintCode}
                onCodeChange={handleCodeChange}
            />
            <ScreenReaderToast ref={toastRef} />
            {mobileFileSelect?.isOpen && (
                <View style={styles.mobileFileSelectMenu}>
                    <MobileFileSelect
                        filenames={cachedCode.current.files.map(
                            (file) => file.filename,
                        )}
                        selectedFilename={selectedFilename}
                        onSelect={onSelectedFilenameChange}
                        onCreateFile={canManageFiles ? createFile : undefined}
                        onRename={canManageFiles ? renameFile : undefined}
                        onDelete={canManageFiles ? deleteFile : undefined}
                        onClose={mobileFileSelect.onClose}
                    />
                </View>
            )}
        </View>
    );
}

function CreateFileButton({
    onClick,
    isCreatingFile,
    hasReachedFileLimit,
}: {
    onClick: () => void;
    isCreatingFile: boolean;
    hasReachedFileLimit: boolean;
}): React.ReactElement {
    const isDisabled = isCreatingFile || hasReachedFileLimit;
    const styles = getStyles(false);
    const button = (
        <IconButton
            icon={plusCircleIcon}
            kind="tertiary"
            size="small"
            aria-label={doNotTranslateYet`Create a new file`}
            disabled={isDisabled}
            onClick={onClick}
            style={[
                styles.createFileButton,
                isDisabled && styles.createFileButtonDisabled,
            ]}
        />
    );

    if (hasReachedFileLimit) {
        return (
            <Tooltip content={strings.maxFilesPerProgram(fileCountLimit)}>
                {button}
            </Tooltip>
        );
    }

    return button;
}
/* eslint-disable react-native/no-unused-styles */
const getStyles = (useEmbeddedStyles: boolean) =>
    StyleSheet.create({
        tabsContainer: {
            padding: 0,
            overflow: "hidden",
            flexGrow: 1,
            position: "relative",
        },
        files: {
            flexGrow: 0,
            flexShrink: 0,
            backgroundColor: color.offWhite,
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-start",
            alignItems: "center",
            paddingLeft: spacing.xxxSmall_4,
            paddingRight: spacing.xxxSmall_4,
            gap: spacing.xxxSmall_4,
            [useEmbeddedStyles
                ? mediaQueries.xsOrSmaller
                : mediaQueries.smOrSmaller]: {
                display: "none",
            },
        },
        tabHeader: {
            padding: spacing.xxxSmall_4,
            gap: spacing.xxSmall_6,
            flexDirection: "row",
            justifyContent: "flex-start",
            overflowX: "auto",
        },
        createFileButton: {
            color: color.offBlack,
            width: spacing.large_24,
            height: spacing.large_24,
        },
        createFileButtonDisabled: {
            color: color.offBlack64,
            ":hover": {
                color: color.offBlack64,
            },
            ":focus": {
                color: color.offBlack64,
            },
        },
        mobileFileSelectMenu: {
            display: "block",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            maxHeight: "100%",
            backgroundColor: color.white,
            overflowY: "auto",
            [useEmbeddedStyles
                ? mediaQueries.smOrLarger
                : mediaQueries.mdOrLarger]: {
                display: "none",
            },
        },
    });

const embeddedStyles = getStyles(true);
const standaloneStyles = getStyles(false);
